import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X } from 'lucide-react';
import type { SourceRef } from './types';

/** "Verified" badge: hover popover on desktop, bottom sheet on mobile, listing the sources. */
const SourcesTag: React.FC<{ refs: SourceRef[]; summary?: string; label?: string }> = ({
  refs,
  summary,
  label = 'Verified',
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    clearTimeout(hoverTimeout.current);
    setShowPopover(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;
    hoverTimeout.current = setTimeout(() => setShowPopover(false), 200);
  }, [isMobile]);

  const list = (
    <>
      {summary && <p className="coeliac-summary">{summary}</p>}
      {refs.map((ref, i) => (
        <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="coeliac-ref-card">
          <span className="coeliac-ref-card-source">{ref.title}</span>
          <span className="coeliac-ref-card-summary">{ref.summary}</span>
        </a>
      ))}
    </>
  );

  return (
    <div className="coeliac-ref-wrapper">
      <button
        className="coeliac-ref-tag"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => isMobile && setShowModal(true)}
        aria-label="View sources"
      >
        <ShieldCheck size={11} />
        <span>{label}</span>
      </button>

      {showPopover && !isMobile && (
        <div className="coeliac-popover" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <div className="coeliac-popover-header">
            <ShieldCheck size={14} />
            <span>Sources</span>
          </div>
          <div className="coeliac-popover-list">{list}</div>
        </div>
      )}

      <AnimatePresence>
        {showModal && isMobile && (
          <motion.div
            className="coeliac-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="coeliac-modal"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="coeliac-modal-header">
                <ShieldCheck size={16} />
                <span>Sources</span>
                <button className="coeliac-modal-close" onClick={() => setShowModal(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className="coeliac-modal-list">{list}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SourcesTag;
